
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/hooks/auth/use-user';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/errors/use-toast';
import { updateUser } from '@/hooks/auth/database-users';

export interface UserProfileImage {
  id: string;
  url: string;
  name: string;
  alt?: string;
}

export const useUserProfileImages = () => {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const [images, setImages] = useState<UserProfileImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchUserProfileImages = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        
        // List all files in the user's directory in the profile_pictures bucket
        const { data: files, error: listError } = await supabase.storage
          .from('profile_pictures')
          .list(user.id);
          
        if (listError) throw listError;
        
        // Map files to profile image objects with public URLs
        if (files && files.length > 0) {
          const profileImages = files.map(file => {
            const filePath = `${user.id}/${file.name}`;
            const { data: { publicUrl } } = supabase.storage
              .from('profile_pictures')
              .getPublicUrl(filePath);
              
            return {
              id: filePath,
              url: publicUrl,
              name: file.name,
              alt: 'Profile picture'
            };
          });
          
          setImages(profileImages);
        }
      } catch (err) {
        console.error('Error fetching user profile images:', err);
        setError(err instanceof Error ? err : new Error('Unknown error occurred'));
      } finally {
        setIsLoading(false);
      }
    };

    if (user?.id) {
      fetchUserProfileImages();
    }
  }, [user?.id]);

  /**
   * Delete a specific image from storage
   * @param imageId The ID of the image to delete (in format userId/filename)
   * @param isCurrentProfilePic Whether this image is the current profile picture
   */
  const deleteImage = useCallback(async (imageId: string, isCurrentProfilePic: boolean): Promise<void> => {
    if (!user?.id) throw new Error("User not authenticated");
    
    try {
      setIsDeleting(true);
      
      // The imageId should be in the format userId/filename
      if (!imageId.startsWith(user.id + '/')) {
        throw new Error('Invalid image ID format');
      }
      
      // Delete the file from storage
      const { error: deleteError } = await supabase.storage
        .from('profile_pictures')
        .remove([imageId]);
        
      if (deleteError) throw deleteError;
      
      // If this is the current profile picture, also update the user record
      if (isCurrentProfilePic) {
        const { error: updateError } = await updateUser(user.id, {
          profile_picture_url: null
        });
        
        if (updateError) throw updateError;
      }
      
      // Update the local state by removing the deleted image
      setImages(prevImages => prevImages.filter(img => img.id !== imageId));
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['profile', user.id] });
      queryClient.invalidateQueries({ queryKey: ['user-profile', user.id] });
      
      toast({
        title: "Image deleted",
        description: isCurrentProfilePic 
          ? "Your profile picture has been removed." 
          : "The image has been deleted from your collection.",
        variant: "success",
      });
    } catch (err) {
      console.error('Error deleting image:', err);
      const message = err instanceof Error ? err.message : 'Failed to delete image';
      
      toast({
        title: "Failed to delete image",
        description: message,
        variant: "error",
      });
      
      throw err;
    } finally {
      setIsDeleting(false);
    }
  }, [user, queryClient]);

  return { 
    images, 
    isLoading, 
    error, 
    isDeleting,
    deleteImage 
  };
};
